
import { Link } from "react-router-dom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/contexts/ProfileContext";
import { Loader2, User2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";
import { useNavigate } from "react-router-dom";

export function ProfileDropdown() {
  const { user, signOut } = useAuth();
  const { profile, isLoading } = useProfile();
  const { toast } = useToast();
  const navigate = useNavigate();

  if (!user) {
    return <Link to="/login" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground transition-colors duration-200 flex items-center gap-2 px-4 py-2 rounded-sm">
        <Avatar className="h-6 w-6">
          <AvatarFallback>
            <User2 className="h-4 w-4" />
          </AvatarFallback>
        </Avatar>
        <span>Sign in / Create Account</span>
      </Link>;
  }

  const userInitial = profile?.full_name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || '?';
  const isCandidate = profile?.role === 'candidate';

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "Signed out successfully",
        className: "bg-card border-none text-foreground"
      });
      navigate("/"); // Redirect to landing page
    } catch (error) {
      console.error('Sign out error:', error);
      toast({
        title: "Error signing out",
        description: "Please try again",
        className: "bg-card border-none text-foreground"
      });
    }
  };

  if (isLoading) {
    return <div className="flex items-center gap-2 px-4 py-2 rounded-sm">
        <Avatar className="h-8 w-8">
          <AvatarFallback className="bg-secondary text-secondary-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </AvatarFallback>
        </Avatar>
        <span className="hidden sm:inline text-muted-foreground">Loading...</span>
      </div>;
  }

  return <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 px-4 py-2 rounded-sm hover:bg-accent transition-colors duration-200">
        <Avatar className="h-8 w-8">
          <AvatarImage src={profile?.avatar_url} />
          <AvatarFallback className="bg-secondary text-secondary-foreground">
            {userInitial}
          </AvatarFallback>
        </Avatar>
        <span className="hidden sm:inline text-foreground">{profile?.full_name || user.email}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 bg-card border-border p-0 shadow-lg relative z-[1000] rounded-sm">
        <div className="p-4 space-y-1.5">
          <div className="font-medium text-foreground">
            {profile?.full_name || 'User'}
          </div>
          <div className="text-sm text-muted-foreground truncate">
            {user.email}
          </div>
          {profile?.role && (
            <div className="mt-2">
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary inline-block">
                {profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
              </span>
            </div>
          )}
        </div>
        
        <Separator className="bg-border" />
        
        <div className="p-1">
          <DropdownMenuItem asChild className="py-2">
            <Link to="/profile" className="cursor-pointer w-full">
              My Profile
            </Link>
          </DropdownMenuItem>
          
          {!isCandidate && (
            <DropdownMenuItem asChild className="py-2">
              <Link to="/billing" className="cursor-pointer w-full">
                Usage & Billing
              </Link>
            </DropdownMenuItem>
          )}
          
          {isCandidate && (
            <DropdownMenuItem asChild className="py-2">
              <Link to="/test-history" className="cursor-pointer w-full flex items-center gap-2">
                Test History
              </Link>
            </DropdownMenuItem>
          )}
        </div>
        
        <Separator className="bg-border" />
        
        <div className="p-1">
          <DropdownMenuItem onClick={handleSignOut} className="py-2 cursor-pointer">
            Sign Out
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>;
}
